import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { createContext } from 'use-context-selector';
import { useNavigate } from 'react-router-dom';
import {
  IconFileText,
  IconNotes,
  IconReport,
  IconScale,
  IconStethoscope,
  IconTable,
  IconTimeline,
  IconClipboardText,
  IconFileDescription,
  IconBook,
  IconBriefcase,
  IconBulb,
  IconListDetails,
  IconFolder,
  IconFileInfo,
  IconFileTypeDoc,
  IconReceipt2,
} from '@tabler/icons-react';

import { NavItemType } from '@app/types';
import { MenuItemsContextValues } from '@app/types/core/menu-items';
import useDataFetcher from '@hooks/useDataFetcher/useDataFetcher';
import { urls } from '@api/urls.ts';
import { MultiDocumentAgentTemplate } from '@app/types/admin';
import { MultiDocumentAgentTemplateType } from '@utils/enums.ts';
import { routes } from '@shared/constants/paths';
import { UseCaseTemplate } from '@app/types/use-case-template';
import { useAuthSelector } from '@providers/AuthProvider/ui/AuthProvider';

const MenuItemsContext = createContext<MenuItemsContextValues | null>(null);

interface UseCaseGroup {
  id: number;
  name: string;
  created_at: string;
  updated_at: string;
}

interface MenuItemsProviderProps {
  initialItems: NavItemType[];
  children: ReactNode;
}

function MenuItemsProvider({ initialItems, children }: MenuItemsProviderProps) {
  const [items, setItems] = useState(initialItems);
  const navigate = useNavigate();

  /**
   * This provider is mounted outside ProtectedRoute, so without the token gate
   * both requests below fire before anyone has signed in, against endpoints
   * that require authentication.
   *
   * The token gate alone was not enough, because the request that poisons the
   * next session is the previous account's own successful one. Everything here
   * is keyed by URL, so both accounts share one cache entry, one 60s deduping
   * window and one mutation timestamp. Signing out and into another account
   * inside that window meant the new mount was deduped onto the old request,
   * and its result was then discarded for being older than the sign-in cache
   * clear. `data` stayed undefined, isValidating was false and
   * revalidateOnFocus is off, so nothing was ever going to fetch again and the
   * sidebar showed only "Home" until the page was reloaded.
   *
   * Scoping by user id gives each account its own cache entry, which has no
   * inflight request and no mutation stamp to inherit. It also means one
   * account's menu can never be served to another.
   */
  const accessToken = useAuthSelector((state) => state.accessToken);
  const userId = useAuthSelector((state) => state.userData?.id ?? null);
  // userData is loaded before the app tree mounts, so both are present here on
  // a normal sign in; requiring the id too keeps a half-ready session from
  // caching a result under the wrong scope.
  const isAuthenticated = Boolean(accessToken && userId);

  const { data, mutate: revalidateTemplates } = useDataFetcher<
    MultiDocumentAgentTemplate[]
  >({
    url: urls.allDocumentTemplates.base,
    params: new URLSearchParams({ is_enabled: 'true' }),
    shouldFetch: isAuthenticated,
    scope: userId,
    revalidateOnFocus: false,
    dedupingInterval: 60000,
  });

  const { data: useCaseGroups, mutate: revalidateGroups } = useDataFetcher<
    UseCaseGroup[]
  >({
    url: urls.useCaseGroup.base,
    shouldFetch: isAuthenticated,
    scope: userId,
    revalidateOnFocus: false,
    dedupingInterval: 60000,
  });

  const revalidate = useCallback(() => {
    revalidateTemplates();
    revalidateGroups();
  }, [revalidateTemplates, revalidateGroups]);

  const value = useMemo(
    () => ({ items, setItems, revalidate }),
    [items, setItems, revalidate]
  );

  // Removed preflight subscription check: direct navigation only

  const handleOptionClick = useCallback(
    async (id: number, template_type: MultiDocumentAgentTemplateType) => {
      if (template_type === MultiDocumentAgentTemplateType.DEPOSITION_SUMMARY) {
        navigate(routes.myDocuments.depositionSummary.create(id));
      } else if (
        template_type === MultiDocumentAgentTemplateType.DEMAND_LETTER
      ) {
        navigate(routes.myDocuments.demandLetter.create(id));
      } else if (template_type === MultiDocumentAgentTemplateType.CUSTOM_GPT) {
        navigate(routes.myDocuments.customGPT.create(id));
      } else if (template_type === MultiDocumentAgentTemplateType.USE_CASE) {
        navigate(routes.myDocuments.useCase.create(id));
      }
    },
    [navigate]
  );

  useEffect(() => {
    if (!data) return;

    // 1) Special-case icons for well-known use case names
    const specialCaseIconMap: Record<string, any> = {
      'demand letter': IconFileText,
      'deposition summary': IconClipboardText,
      'lost wages': IconReceipt2,
      'medical billing table': IconTable,
      'medical timeline': IconTimeline,
      'pain track': IconStethoscope,
    };

    // 1b) Special-case colors for those well-known names (tabler icon fill color)
    const specialCaseColorMap: Record<string, string> = {
      'demand letter': '#3B82F6', // blue-500
      'deposition summary': '#10B981', // emerald-500
      'lost wages': '#F59E0B', // amber-500
      'medical billing table': '#8B5CF6', // violet-500
      'medical timeline': '#EC4899', // pink-500
      'pain track': '#EF4444', // red-500
    };

    // 2) A broad pool of distinct icons for automatic assignment
    const iconPoolBase = [
      IconNotes,
      IconReport,
      IconScale,
      IconStethoscope,
      IconTable,
      IconTimeline,
      IconClipboardText,
      IconFileDescription,
      IconBook,
      IconBriefcase,
      IconBulb,
      IconListDetails,
      IconFolder,
      IconFileInfo,
      IconFileTypeDoc,
      IconReceipt2,
    ];

    // 2b) A pool of distinct, accessible colors (kept bright and readable)
    const colorPoolBase: string[] = [
      '#0EA5E9', // sky-500
      '#22C55E', // green-500
      '#F59E0B', // amber-500
      '#EF4444', // red-500
      '#8B5CF6', // violet-500
      '#EC4899', // pink-500
      '#14B8A6', // teal-500
      '#F43F5E', // rose-500
      '#A855F7', // purple-500
      '#EAB308', // yellow-500
      '#6366F1', // indigo-500
      '#06B6D4', // cyan-500
    ];

    // 3) Deterministic hash for a string (djb2-like)
    const hash = (str: string) => {
      let h = 5381;
      for (let i = 0; i < str.length; i += 1) {
        h = (h * 33) ^ str.charCodeAt(i);
      }
      return Math.abs(h);
    };

    // 4) First pass: mark used icons from special-case assignments present in data
    const usedIcons = new Set<any>();
    const usedColors = new Set<string>();
    data.forEach((template) => {
      const key = (template.name || `${template.id}`).trim().toLowerCase();
      const special = specialCaseIconMap[key];
      if (special) usedIcons.add(special);
      const specialColor = specialCaseColorMap[key];
      if (specialColor) usedColors.add(specialColor);
    });

    // 5) Remove used (special-case) icons from the pool to avoid duplicates
    let iconPool = iconPoolBase.filter((icon) => !usedIcons.has(icon));
    let colorPool = colorPoolBase.filter((c) => !usedColors.has(c));

    // 6) Helper to pick a unique icon from the remaining pool
    const pickFromPool = (name: string) => {
      if (iconPool.length === 0) return IconFileText; // fallback
      const key = name.trim().toLowerCase();
      const idx = hash(key) % iconPool.length;
      // Select & remove to keep uniqueness
      const chosen = iconPool[idx];
      usedIcons.add(chosen);
      iconPool = iconPool.filter((_, i) => i !== idx);
      return chosen || IconFileText;
    };

    // 6b) Pick a color deterministically as well
    const pickColorFromPool = (name: string) => {
      if (colorPool.length === 0) return '#64748B'; // slate-500 fallback
      const key = name.trim().toLowerCase();
      const idx = hash(`${key}-color`) % colorPool.length;
      const chosen = colorPool[idx];
      usedColors.add(chosen);
      colorPool = colorPool.filter((_, i) => i !== idx);
      return chosen || '#64748B';
    };

    // 7) Build menu items with grouping logic
    const templatesWithoutGroup: NavItemType[] = [];
    const groupedTemplates = new Map<number, NavItemType[]>();

    data.forEach((template) => {
      const displayName = template.name || `${template.id}`;
      const key = displayName.trim().toLowerCase();
      const special = specialCaseIconMap[key];
      const IconComponent = special || pickFromPool(displayName);
      const iconColor =
        specialCaseColorMap[key] || pickColorFromPool(displayName);

      const menuItem: NavItemType = {
        id: `${String(template.id)}-${template.template_type}`,
        title: template.name,
        type: 'item',
        url: `/create/${template.id}`,
        customAction: () =>
          handleOptionClick(template.id, template.template_type),
        icon: IconComponent,
        iconColor,
        breadcrumbs: false,
      };

      // Check if template has a use_case_group
      const useCaseTemplate = template as UseCaseTemplate;
      if (useCaseTemplate.use_case_group != null) {
        const groupId = useCaseTemplate.use_case_group;
        if (!groupedTemplates.has(groupId)) {
          groupedTemplates.set(groupId, []);
        }
        groupedTemplates.get(groupId)!.push(menuItem);
      } else {
        templatesWithoutGroup.push(menuItem);
      }
    });

    // 8) Build the final menu structure with groups
    const newItems: NavItemType[] = [];

    // Add grouped items as group sections with children
    if (useCaseGroups) {
      useCaseGroups.forEach((group) => {
        const groupTemplates = groupedTemplates.get(group.id);
        if (groupTemplates && groupTemplates.length > 0) {
          newItems.push({
            id: `group-${group.id}`,
            title: group.name,
            type: 'group',
            children: groupTemplates,
          });
        }
      });
    }

    // Add ungrouped templates directly without a group wrapper
    newItems.push(...templatesWithoutGroup);

    setItems((prevItems) => {
      // Keep only non-template items (like bottom menu items: My Documents, Notifications, etc.)
      // Remove: groups (group-*), step1, and any items with URL starting with /create/
      const updatedItems = prevItems.filter((item) => {
        if (!item.id) return true;
        if (item.id.startsWith('group-')) return false;
        if (item.id === 'step1') return false;
        // Remove any template items (they have URLs starting with /create/)
        if (item.url?.startsWith('/create/')) return false;
        return true;
      });

      const newItemsList = [...updatedItems, ...newItems];

      // Only update if items actually changed
      if (JSON.stringify(prevItems) === JSON.stringify(newItemsList)) {
        return prevItems;
      }

      return newItemsList;
    });
  }, [data, useCaseGroups, handleOptionClick]);

  return (
    <MenuItemsContext.Provider value={value}>
      {children}
    </MenuItemsContext.Provider>
  );
}

export { MenuItemsContext, MenuItemsProvider };
